<template>
	<v-card class="d-flex flex-column">
		<v-toolbar color="primary" flat>
			<v-toolbar-title>{{ title }}</v-toolbar-title>
			<v-spacer />
			<v-hover-icon class="mr-1" />
			<v-btn icon size="small" title="Close" @click="$emit('close')">
				<v-icon>mdi-close</v-icon>
			</v-btn>
		</v-toolbar>

		<v-card-text class="pa-4">
			<div class="text-body-2 text-grey-darken-1 mb-4">
				This is the full list of Dactyl roles and their permissions. Users can,
				and often do, belong to multiple roles, granting different combinations
				of permissions to various aspects of Dactyl.
			</div>

			<v-progress-linear v-if="loading" color="secondary" indeterminate />

			<div
				v-else-if="!sortedRoles.length"
				class="text-body-2 text-grey-darken-1"
			>
				No roles found.
			</div>

			<v-expansion-panels v-else variant="accordion" multiple>
				<v-expansion-panel v-for="role of sortedRoles" :key="role.id">
					<v-expansion-panel-title
						:readonly="isAdministrator(role)"
						:ripple="!isAdministrator(role)"
						:class="{ 'access-level-static': isAdministrator(role) }"
					>
						<div class="d-flex align-center ga-4 w-100 pr-4">
							<div class="d-flex flex-column flex-grow-1 access-level-summary">
								<span class="font-weight-bold access-level-name">
									{{ role.name }}
								</span>
								<span
									v-if="role.description"
									class="text-body-2 text-grey-darken-1"
								>
									{{ role.description }}
								</span>
							</div>
							<span class="text-body-2 text-grey-darken-1 flex-shrink-0">
								{{ userCountLabel(role) }}
							</span>
						</div>

						<template v-if="isAdministrator(role)" #actions>
							<v-icon class="access-level-chevron-placeholder" />
						</template>
					</v-expansion-panel-title>

					<v-expansion-panel-text v-if="!isAdministrator(role)">
						<permission-grant-list :permissions="role.permissions" columns />
					</v-expansion-panel-text>
				</v-expansion-panel>
			</v-expansion-panels>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import pluralize from "@/utils/pluralize";
import VHoverIcon from "@/components/VHoverIcon";
import PermissionGrantList from "@/pages/UserManager/PermissionGrantList";

// Least to most permissive. A role not listed here (one created later in
// RoleEdit) sorts by name just ahead of the two admin roles.
const ROLE_ORDER = [
	"prospect",
	"training_curator",
	"curator",
	"reviewer",
	"paper_manager",
	"interview_manager",
	"group_lead",
	"organization_admin",
	"administrator",
];
const UNLISTED_ROLE_RANK = ROLE_ORDER.indexOf("organization_admin") - 0.5;

// Every live role, what it grants and how many users hold it, so whoever
// manages access can see what a role hands out before assigning it. The
// content of the Access levels dialog opened from the Users toolbar.
export default {
	name: "AccessLevels",

	components: {
		PermissionGrantList,
		VHoverIcon,
	},

	emits: ["close"],

	data() {
		return {
			loading: false,
			roles: [],
			title: "Access levels",
			// role id -> number of users holding it; absent while loading or if
			// the count failed.
			userCounts: {},
		};
	},

	computed: {
		sortedRoles() {
			const rank = (role) => {
				const index = ROLE_ORDER.indexOf(role.slug);
				return index === -1 ? UNLISTED_ROLE_RANK : index;
			};
			return [...this.roles].sort(
				(a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
			);
		},
	},

	mounted() {
		this.refresh();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		refresh() {
			this.loading = true;
			this.$http
				.get("/roles", { params: { omitDeleted: true } })
				.then((response) => {
					this.roles = response.data.roles.data;
					this.fetchUserCounts();
				})
				.catch((error) => {
					console.error("[AccessLevels] roles", error);
					this.showNotification({
						message: "Could not load the access levels.",
						status: "error",
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},
		// One count per role through GET /users, the same query the Users
		// table runs when filtered to that role (non-deleted users, scoped to
		// the caller's organizations for non-admins), so the count always
		// matches what that filter lists.
		fetchUserCounts() {
			this.userCounts = {};
			for (const role of this.roles) {
				this.$http
					.get("/users", {
						params: {
							omitDeleted: true,
							role: role.id,
							page: 1,
							itemsPerPage: 1,
						},
					})
					.then((response) => {
						this.userCounts = {
							...this.userCounts,
							[role.id]: parseInt(response.data.users.meta.total, 10),
						};
					})
					.catch((error) => {
						// The label falls back to no count; the role and what it
						// grants still render.
						console.error("[AccessLevels] user count", role.slug, error);
					});
			}
		},
		// Administrator skips every permission check, so its row is the
		// description alone and does not expand.
		isAdministrator(role) {
			return role.slug === "administrator";
		},
		userCountLabel(role) {
			const count = this.userCounts[role.id];
			if (count === undefined) {
				return "";
			}
			return pluralize("user", count, true);
		},
	},
};
</script>

<style scoped lang="scss">
.access-level-summary {
	min-width: 0;
}

.access-level-name {
	font-size: 1rem;
}

.access-level-static {
	cursor: default;

	:deep(.v-expansion-panel-title__overlay) {
		display: none;
	}
}

// Keeps the user count aligned with the rows that show a chevron.
.access-level-chevron-placeholder {
	visibility: hidden;
}
</style>
